// What a city banner says beyond its name: at war, near the front, occupied. Every mark carries the
// sources of the record that justifies it, and a mark no record backs is not drawn (truth rule 1).
//
//   war       the city's country is a theatre of an active major      ◀ War.theatre, War.sources
//             interstate or civil war whose intensity claim is dated
//             on or before the date (an insurgency has no place we know: no city is marked)
//   nearFront a sourced front line lies within reach and its         ◀ snapshot.fronts (sources)
//             controller is not the state holding the city. A distance, not a siege: no source
//             we cite says "besieged", so the mark never does.
//   occupied  snapshot.control says OCCUPIED on land of the city's    ◀ the control episodes holding the
//             own country, at least half of the hex held by another     hex (`controlSources`); until the
//             state                                                     snapshot gives them, the occupier's
//                                                                       fronts. Never the wars: a war says
//                                                                       where it is fought, not who holds what.
//
// A city belongs to the country its record names (`iso3`), not to whoever owns the hex it stands on:
// a hex is ~80 km wide, so Monaco, Singapore, Jerusalem or a harbour city on a sea hex land in a
// neighbour's or nobody's hex. `citySite` resolves the home state, the land hex to read, and the occupier.

import type { SourceId, War } from '../../core/schema.ts';
import { WORLD_W, hexCenter, hexCol, hexRow, neighbour } from '../../core/grid.ts';
import { CONTROL } from '../../world/api.ts';
import { segmentDistance } from './frontGeo.ts';
import type { FrontRecord } from './fronts.ts';

export type MarkKind = 'war' | 'nearFront' | 'occupied';

export interface Mark {
  kind: MarkKind;
  sources: SourceId[];
}

export interface StatusInput {
  /** Snapshot date: a war claim dated after it says nothing about it. */
  date: string;
  owner: Uint16Array;
  controller: Uint16Array;
  control: Uint8Array;
  /** 0..255 share of each hex held by its controller. */
  controlFrac: Uint8Array;
  wars: ReadonlyArray<Pick<War, 'theatre' | 'sources' | 'kind' | 'intensity'>>;
  fronts: readonly FrontRecord[];
  /** Actor index of a country (city.iso3); 0 when unknown. Absent = every country unknown. */
  actorOf?: (iso3: string) => number;
  /**
   * Sources of the control episodes that hold `hex` for `controller`. When given it is the only
   * source of an occupied mark (none = no mark); absent = the occupier's fronts stand in.
   */
  controlSources?: (hex: number, controller: number) => readonly SourceId[];
}

/** Where a city really is: its state, the land hex to read, and who occupies that hex (0 = nobody). */
export interface CitySite {
  home: number;
  hex: number;
  occupier: number;
}

// Wars that reach every city of a theatre country; smaller kinds are local and unplaced.
const CITY_WAR_KINDS: ReadonlyArray<War['kind']> = ['interstate', 'civil'];
const CITY_WAR_INTENSITY: War['intensity']['value'] = 'major';

// A front line closer than this (world units, about 1.5 hexes) is "near the front".
export const NEAR_FRONT_REACH = 2.6;

// A city is occupied when the occupier holds at least half of its hex (controlFrac, 0..255).
const OCCUPIED_FRAC = 128;
// A city on a hex nobody owns (sea, lake) reads the nearest owned hex within this many rings.
const LAND_RINGS = 3;
const NEIGHBOURS = 6;

/**
 * The nearest hex within LAND_RINGS that `home` owns (any owned hex when `home` owns none there);
 * the hex itself when it is owned or nothing is owned nearby. Rings grow by breadth-first steps.
 */
function landHex(hex: number, home: number, owner: Uint16Array): number {
  if (owner[hex] > 0) {
    return hex;
  }

  const seen = new Set<number>([hex]);
  let ring = [hex];
  let nearest = -1;

  for (let r = 0; r < LAND_RINGS; r++) {
    const next: number[] = [];
    for (const h of ring) {
      for (let d = 0; d < NEIGHBOURS; d++) {
        const n = neighbour(h, d);
        if (n < 0 || seen.has(n)) {
          continue;
        }

        seen.add(n);
        next.push(n);
      }
    }

    const mine = next.find((n) => home > 0 && owner[n] === home);
    if (mine !== undefined) {
      return mine;
    }

    nearest = nearest >= 0 ? nearest : (next.find((n) => owner[n] > 0) ?? -1);
    ring = next;
  }

  return nearest >= 0 ? nearest : hex;
}

/** Home state, land hex and occupier of the city on `hex` in `iso3`'s country. */
export function citySite(hex: number, iso3: string, input: StatusInput): CitySite {
  const known = input.actorOf?.(iso3) ?? 0;
  const at = landHex(hex, known, input.owner);
  const home = known || input.owner[at];

  // The hex's control record speaks for the city only on land of the city's own country.
  const taker = input.controller[at];
  const taken = input.owner[at] === home && input.control[at] === CONTROL.OCCUPIED && input.controlFrac[at] >= OCCUPIED_FRAC;
  return { home, hex: at, occupier: taken && taker > 0 && taker !== home ? taker : 0 };
}

/** Marks of the city on `hex`, in `iso3`'s country, for one snapshot. */
export function cityMarks(hex: number, iso3: string, input: StatusInput): Mark[] {
  const marks: Mark[] = [];
  // The intensity is a claim dated `as_of`: read at an earlier date it is unknown, not 'major' (see warViewAt).
  const wars = input.wars.filter((w) => w.theatre.includes(iso3) && CITY_WAR_KINDS.includes(w.kind) && w.intensity.value === CITY_WAR_INTENSITY && w.intensity.as_of <= input.date);
  if (wars.length > 0) {
    marks.push({ kind: 'war', sources: uniq(wars.flatMap((w) => w.sources)) });
  }

  const site = citySite(hex, iso3, input);
  const onOwnSoil = input.owner[site.hex] === site.home;
  const holder = onOwnSoil ? input.controller[site.hex] || input.owner[site.hex] : site.home;
  const at = hexCenter(hexCol(site.hex), hexRow(site.hex));
  const pressing = input.fronts.filter((f) => f.actor > 0 && f.actor !== holder && reaches(f, at.x, at.z));
  if (pressing.length > 0) {
    marks.push({ kind: 'nearFront', sources: uniq(pressing.flatMap((f) => f.sources)) });
  }

  if (site.occupier === 0) {
    return marks;
  }

  const sources = occupierSources(site, input);
  if (sources.length > 0) {
    marks.push({ kind: 'occupied', sources });
  }

  return marks;
}

/** Sources behind an occupied mark: the control episodes of the hex, else the occupier's fronts. */
function occupierSources(site: CitySite, input: StatusInput): SourceId[] {
  if (input.controlSources) {
    return uniq(input.controlSources(site.hex, site.occupier));
  }

  const held = input.fronts.filter((f) => f.actor > 0 && f.actor === site.occupier);
  return uniq(held.flatMap((f) => f.sources));
}

function uniq<T>(items: readonly T[]): T[] {
  return [...new Set(items)];
}

/** Whether any line of the front passes within NEAR_FRONT_REACH of (x, z). */
function reaches(front: FrontRecord, x: number, z: number): boolean {
  for (const line of front.lines) {
    // Pick the copy of the point (±WORLD_W) nearest the line, which is continuous across the seam.
    const px = x + Math.round((line[0] - x) / WORLD_W) * WORLD_W;
    for (let i = 2; i < line.length; i += 2) {
      if (segmentNear(px, z, line[i - 2], line[i - 1], line[i], line[i + 1])) {
        return true;
      }
    }
  }
  return false;
}

function segmentNear(px: number, pz: number, ax: number, az: number, bx: number, bz: number): boolean {
  // Bounding box first: most segments are nowhere near.
  if (Math.max(ax, bx) < px - NEAR_FRONT_REACH || Math.min(ax, bx) > px + NEAR_FRONT_REACH) {
    return false;
  }
  if (Math.max(az, bz) < pz - NEAR_FRONT_REACH || Math.min(az, bz) > pz + NEAR_FRONT_REACH) {
    return false;
  }

  return segmentDistance(px, pz, ax, az, bx, bz) <= NEAR_FRONT_REACH;
}
